import { type PropsWithChildren } from 'preact/compat';
import { useCallback, useEffect, useRef } from 'preact/hooks';

export type OutsideFocusHandlerProps = PropsWithChildren<{
    onClickOutside?: (event: MouseEvent | TouchEvent) => void;
    onFocusOutside?: (event: FocusEvent) => void;
}>;

const OutsideFocusHandler = (props: OutsideFocusHandlerProps) => {
    const clickCaptured = useRef(false);
    const focusCaptured = useRef(false);

    const innerClick = useCallback(() => {
        clickCaptured.current = true;
    }, []);
    const innerFocus = useCallback(() => {
        focusCaptured.current = true;
    }, []);
    const documentClick = useCallback(
        (event: MouseEvent | TouchEvent) => {
            if (!clickCaptured.current && props.onClickOutside) {
                props.onClickOutside(event);
            }
            if (clickCaptured.current) {
                clickCaptured.current = false;
            }
        },
        [clickCaptured.current]
    );
    const documentFocus = useCallback(
        (event: FocusEvent) => {
            if (!focusCaptured.current && props.onFocusOutside) {
                props.onFocusOutside(event);
            }
            if (focusCaptured.current) {
                focusCaptured.current = false;
            }
        },
        [focusCaptured.current]
    );

    useEffect(() => {
        document.addEventListener('mousedown', documentClick);
        document.addEventListener('focusin', documentFocus);
        document.addEventListener('touchstart', documentClick);
        return () => {
            document.removeEventListener('mousedown', documentClick);
            document.removeEventListener('focusin', documentFocus);
            document.removeEventListener('touchstart', documentClick);
        };
    }, [documentClick, documentFocus]);

    return (
        <span
            onMouseDown={innerClick}
            onFocusIn={innerFocus}
            onTouchStart={innerClick}
        >
            {props.children}
        </span>
    );
};

export default OutsideFocusHandler;
